import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { clearRoutes, setLoading, setHeaderLoading } from "../../store/slices/travelSlice";
import { ArrowLeftRight, Train, Plane, Bus, Plus, Minus, LayoutGrid, X, Calendar, ChevronDown } from 'lucide-react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import TravelAutocompleteInput from "../search-form/TravelAutocompleteInput";
import { toast } from 'react-toastify';
import { FEATURES } from '../../config/features';

const FormSearchSection = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Helper to get initial search data from localStorage
    const getInitialSearchData = () => {
        const savedSearch = localStorage.getItem("last_form_search");
        if (savedSearch) {
            try {
                return JSON.parse(savedSearch);
            } catch (e) {
                console.error("Error parsing saved search:", e);
            }
        }
        return null;
    };

    const initialData = getInitialSearchData();

    const [aiSearchInput, setAiSearchInput] = useState("");
    const [activeModes, setActiveModes] = useState(initialData?.activeModes || ['All']);
    const [showTravelersModal, setShowTravelersModal] = useState(false);
    const [passengerCounts, setPassengerCounts] = useState(initialData?.passengerCounts || {
        adults: 1,
        children: 0,
        infants: 0
    });
    const [seatClasses, setSeatClasses] = useState(initialData?.seatClasses || ['Economy', '3A']);
    const [travelDate, setTravelDate] = useState(() => {
        if (initialData?.travelDate) {
            const parsedDate = new Date(initialData.travelDate);
            if (parsedDate >= new Date().setHours(0, 0, 0, 0)) {
                return parsedDate;
            }
        }
        return null;
    });
    const [fromLocation, setFromLocation] = useState(initialData?.fromLocation || "");
    const [toLocation, setToLocation] = useState(initialData?.toLocation || "");
    const [fromLocationData, setFromLocationData] = useState(initialData?.fromLocationData || null);
    const [toLocationData, setToLocationData] = useState(initialData?.toLocationData || null);
    const [nonStopOnly, setNonStopOnly] = useState(initialData?.nonStopOnly ?? false);

    const travelersRef = React.useRef(null);

    // Smart Resolution of locations on mount (for persistent search)
    React.useEffect(() => {
        const POPULAR_LOCS = [
            { name: "Indira Gandhi Intl Airport", code: "DEL", city: "Delhi", type: "airport", label: "Delhi (DEL)", subLabel: "IGI Airport", lat: 28.5562, lng: 77.1000 },
            { name: "New Delhi", code: "NDLS", city: "Delhi", type: "station", label: "New Delhi (NDLS)", subLabel: "Railway Station", lat: 28.6141, lng: 77.2144 },
            { name: "Chhatrapati Shivaji Intl", code: "BOM", city: "Mumbai", type: "airport", label: "Mumbai (BOM)", subLabel: "International Airport", lat: 19.0887, lng: 72.8679 },
            { name: "Mumbai Central", code: "MMCT", city: "Mumbai", type: "station", label: "Mumbai Central (MMCT)", subLabel: "Railway Station", lat: 18.9698, lng: 72.8193 },
            { name: "Kempegowda Intl Airport", code: "BLR", city: "Bangalore", type: "airport", label: "Bangalore (BLR)", subLabel: "International Airport", lat: 13.1989, lng: 77.7068 },
            { name: "Ahmedabad Airport", code: "AMD", city: "Ahmedabad", type: "airport", label: "Ahmedabad (AMD)", subLabel: "Airport", lat: 23.0734, lng: 72.6266 },
            { name: "Ahmedabad Junction", code: "ADI", city: "Ahmedabad", type: "station", label: "Ahmedabad (ADI)", subLabel: "Railway Station", lat: 23.0273, lng: 72.5950 },
            { name: "KSR Bengaluru City", code: "SBC", city: "Bengaluru", type: "station", label: "Bengaluru (SBC)", subLabel: "Railway Station", lat: 12.9774, lng: 77.5728 },
            { name: "Chennai Central", code: "MAS", city: "Chennai", type: "station", label: "Chennai Central (MAS)", subLabel: "Railway Station", lat: 13.0827, lng: 80.2707 },
            { name: "Netaji Subhash Chandra Bose Intl", code: "CCU", city: "Kolkata", type: "airport", label: "Kolkata (CCU)", subLabel: "International Airport", lat: 22.6547, lng: 88.4467 },
            { name: "Pune Junction", code: "PUNE", city: "Pune", type: "station", label: "Pune Jn (PUNE)", subLabel: "Railway Station", lat: 18.5289, lng: 73.8744 },
            { name: "Hyderabad Deccan", code: "HYB", city: "Hyderabad", type: "station", label: "Hyderabad (HYB)", subLabel: "Railway Station", lat: 17.3916, lng: 78.4684 }
        ];

        const resolve = (txt, data) => {
            if (txt && !data) {
                // Try to match popular first
                const match = POPULAR_LOCS.find(l =>
                    l.city.toLowerCase() === txt.toLowerCase() ||
                    l.label.toLowerCase() === txt.toLowerCase() ||
                    l.name.toLowerCase() === txt.toLowerCase()
                );
                if (match) return match;

                // Fallback: If it's a typed value from a successful search, 
                // wrap it in a placeholder so the chip background (bg) appears.
                // The results page will handle the name resolution.
                return { label: txt, city: txt, type: 'search-match' };
            }
            return data;
        };

        const resFrom = resolve(fromLocation, fromLocationData);
        if (resFrom && resFrom !== fromLocationData) {
            setFromLocationData(resFrom);
            if (resFrom.label) setFromLocation(resFrom.label);
        }

        const resTo = resolve(toLocation, toLocationData);
        if (resTo && resTo !== toLocationData) {
            setToLocationData(resTo);
            if (resTo.label) setToLocation(resTo.label);
        }
    }, []);

    React.useEffect(() => {
        // Sync travelers and seat classes logic if needed
    }, [activeModes]);

    const dropdownRef = useRef(null);

    React.useEffect(() => {
        const handleOutside = (event) => {
            const inTrigger = travelersRef.current && travelersRef.current.contains(event.target);
            const inDropdown = dropdownRef.current && dropdownRef.current.contains(event.target);
            if (!inTrigger && !inDropdown) {
                setShowTravelersModal(false);
            }
        };

        // mousedown for desktop, touchend for mobile (touchstart races with onClick)
        document.addEventListener("mousedown", handleOutside);
        document.addEventListener("touchend", handleOutside, { passive: true });
        return () => {
            document.removeEventListener("mousedown", handleOutside);
            document.removeEventListener("touchend", handleOutside);
        };
    }, []);

    // Toggle Mode Logic
    const toggleMode = (mode) => {
        if (mode === 'All') {
            setActiveModes(['All']);
            return;
        }

        setActiveModes(prev => {
            let newModes = [...prev];

            // If currently All is selected, clear it and start with the new mode
            if (newModes.includes('All')) {
                newModes = [];
            }

            if (newModes.includes(mode)) {
                // Deselect logic: prevent deselecting the last mode (optional, but good UX)
                // or replicate "allow empty" which defaults to something? 
                // User requirement: "all else can be selected independently"
                newModes = newModes.filter(m => m !== mode);
            } else {
                newModes.push(mode);
            }

            // If we ended up empty, default back to 'All' or just leave it?
            // "only ALL when selected no other can be left selected"
            // Let's enforce at least one selection? Or default to All if empty.
            if (newModes.length === 0) return ['All'];

            return newModes;
        });
    };

    // Toggle seat class selection (multi-select)
    const toggleSeatClass = (cls) => {
        setSeatClasses(prev => {
            const current = Array.isArray(prev) ? prev : [];
            const isTrainClass = ['1A', '2A', '3A', 'SL'].includes(cls);
            const isFlightClass = ['Economy', 'Premium', 'Business', 'First'].includes(cls);

            if (isTrainClass) {
                const withoutTrain = current.filter(c => !['1A', '2A', '3A', 'SL'].includes(c));
                return [...withoutTrain, cls];
            }
            if (isFlightClass) {
                const withoutFlight = current.filter(c => !['Economy', 'Premium', 'Business', 'First'].includes(c));
                return [...withoutFlight, cls];
            }
            return current;
        });
    };

    // Lock body scroll when travelers modal is open
    React.useEffect(() => {
        if (showTravelersModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [showTravelersModal]);

    const isFormValid = fromLocation && toLocation && travelDate;

    const handleAISearch = (e) => {
        e.preventDefault();
        navigate("/chat", { state: { fromHero: true, heroInput: aiSearchInput } });
    };

    return (
        <section className="home-search-section">
            {/* AI Search Bar - Always Visible */}
            <div className="search-bar" style={{ margin: 0 }}>
                <form onSubmit={handleAISearch} style={{ display: "flex", width: "100%", alignItems: "center" }}>
                    <div className="search-input" style={{ flex: 1 }}>
                        <img src="/Star1.svg" alt="AI" />
                        <input
                            type="text"
                            value={aiSearchInput}
                            onChange={e => setAiSearchInput(e.target.value)}
                            placeholder="Darbhanga se Mumbai jane ke liye kal ki date ke liye routes batao"
                            style={{
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                width: '100%'
                            }}
                        />
                    </div>
                    <button className="search-button" type="submit">
                        <img src="/send.svg" alt="Send" />
                    </button>
                </form>
            </div>

            {/* Divider */}
            <div className="form-divider-or">
                <div className="divider-line"></div>
                OR
                <div className="divider-line"></div>
            </div>

            {/* Advanced Form Search */}
            <div className="form-search-container">
                <div className="home-search-bar-wrapper" style={{ width: '100%', position: 'relative' }}>
                    <div className="home-search-bar">
                        <div className="search-field home-search-main-field" style={{ position: 'relative' }}>
                            <div className="home-input-wrapper">
                                <span className="mobile-label">From</span>
                                {fromLocationData ? (
                                    <div className="home-location-tag">
                                        <span className="home-location-name">{fromLocation.split('(')[0].trim()}</span>
                                        <button className="home-tag-clear-btn" onClick={() => {
                                            setFromLocation("");
                                            setFromLocationData(null);
                                        }}>
                                            <X size={16} />
                                        </button>
                                    </div>
                                ) : (
                                    <TravelAutocompleteInput
                                        className="search-field-input"
                                        placeholder="From"
                                        value={fromLocation}
                                        onChange={(val) => {
                                            setFromLocation(val);
                                            setFromLocationData(null);
                                        }}
                                        onSelect={(item) => {
                                            setFromLocation(item.label);
                                            setFromLocationData(item);
                                        }}
                                    />
                                )}
                            </div>

                            {/* Swap button — native <button> guarantees tap events on mobile.
                                Absolutely centred between FROM and TO rows. */}
                            <button
                                type="button"
                                className="swap-btn"
                                style={{
                                    position: 'absolute',
                                    left: '50%',
                                    top: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    zIndex: 200,
                                    background: '#fff',
                                    border: '1px solid #E2E8F0',
                                    borderRadius: '50%',
                                    width: 40,
                                    height: 40,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    WebkitTapHighlightColor: 'transparent',
                                    touchAction: 'manipulation',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                    padding: 0,
                                }}
                                onClick={() => {
                                    const temp = fromLocation;
                                    const tempData = fromLocationData;
                                    setFromLocation(toLocation);
                                    setFromLocationData(toLocationData);
                                    setToLocation(temp);
                                    setToLocationData(tempData);
                                }}
                            >
                                <ArrowLeftRight size={20} />
                            </button>

                            <div className="home-input-wrapper">
                                <span className="mobile-label">To</span>
                                {toLocationData ? (
                                    <div className="home-location-tag">
                                        <span className="home-location-name">{toLocation.split('(')[0].trim()}</span>
                                        <button className="home-tag-clear-btn" onClick={() => {
                                            setToLocation("");
                                            setToLocationData(null);
                                        }}>
                                            <X size={16} />
                                        </button>
                                    </div>
                                ) : (
                                    <TravelAutocompleteInput
                                        className="search-field-input"
                                        placeholder="To"
                                        value={toLocation}
                                        onChange={(val) => {
                                            setToLocation(val);
                                            setToLocationData(null);
                                        }}
                                        onSelect={(item) => {
                                            setToLocation(item.label);
                                            setToLocationData(item);
                                        }}
                                    />
                                )}
                            </div>
                        </div>

                        <div className="search-field-sep"></div>

                        <div className="search-field date-picker-container">
                            <span className="mobile-label">Departure</span>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', cursor: 'pointer' }}>
                                <DatePicker
                                    selected={travelDate}
                                    onChange={(date) => setTravelDate(date)}
                                    dateFormat="MMM dd/eee"
                                    className="search-field-input"
                                    placeholderText="Select date"
                                    minDate={new Date()}
                                    isClearable={false}
                                    popperPlacement="bottom"
                                />
                                <Calendar size={18} color="#64748B" className="mobile-only-icon" style={{ flexShrink: 0 }} />
                            </div>
                        </div>

                        <div className="search-field-sep"></div>

                        <div
                            className="search-field travelers-field"
                            ref={travelersRef}
                            onClick={() => setShowTravelersModal(true)}
                            style={{ cursor: 'pointer' }}
                        >
                            <span className="mobile-label">Travelers & Class</span>
                            <div className="search-field-input" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                <span style={{
                                    flex: 1, textAlign: 'left', minWidth: 0,
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                }}>
                                    {(() => {
                                        const showTrain = activeModes.includes('Train') || activeModes.includes('All');
                                        const showFlight = activeModes.includes('Flight') || activeModes.includes('All');
                                        const fl = seatClasses.filter(c => ['Economy', 'Premium', 'Business', 'First'].includes(c));
                                        const tr = seatClasses.filter(c => ['1A', '2A', '3A', 'SL'].includes(c));
                                        const { adults = 1, children = 0, infants = 0 } = passengerCounts;
                                        const pax = [`${adults} adult${adults > 1 ? 's' : ''}`];
                                        if (children > 0) pax.push(`${children} child${children > 1 ? 'ren' : ''}`);
                                        if (infants > 0) pax.push(`${infants} infant${infants > 1 ? 's' : ''}`);
                                        return (
                                            <>
                                                <span>{pax.join(', ')}</span>
                                                {showFlight && fl.length > 0 && (<><span style={{ color: '#CBD5E1' }}>·</span><Plane size={15} style={{ flexShrink: 0, color: '#64748B' }} /><span>{fl.join(', ')}</span></>)}
                                                {showTrain && tr.length > 0 && (<><span style={{ color: '#CBD5E1' }}>·</span><Train size={15} style={{ flexShrink: 0, color: '#64748B' }} /><span>{tr.join(', ')}</span></>)}
                                            </>
                                        );
                                    })()}
                                </span>
                                <span style={{
                                    flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 3,
                                    marginLeft: 14, paddingLeft: 12, borderLeft: '1px solid #E2E8F0',
                                    color: '#FF4E01', fontFamily: "'Inter', sans-serif",
                                    fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                                }}>
                                    Change
                                    <ChevronDown size={14} style={{ flexShrink: 0 }} />
                                </span>
                            </div>

                            
                        </div>
                    </div>
                </div>

                <div className="modes-container">
                    <span>Preferred modes</span>
                    <div className="modes-tabs">
                        <button className={`mode-btn ${activeModes.includes('All') ? 'active' : ''}`} onClick={() => toggleMode('All')}>
                            <LayoutGrid size={20} /> All
                        </button>
                        <button className={`mode-btn ${activeModes.includes('Train') ? 'active' : ''}`} onClick={() => toggleMode('Train')}>
                            <Train size={20} /> Train
                        </button>
                        <button className={`mode-btn ${activeModes.includes('Flight') ? 'active' : ''}`} onClick={() => toggleMode('Flight')}>
                            <Plane size={20} /> Flight
                        </button>
                        {FEATURES.ENABLE_BUS && (
                            <button className={`mode-btn ${activeModes.includes('Bus') ? 'active' : ''}`} onClick={() => toggleMode('Bus')}>
                                <Bus size={20} /> Bus
                            </button>
                        )}
                    </div>

                    <div className="options-row">
                        <label className="non-stop">
                            <input
                                type="checkbox"
                                checked={nonStopOnly}
                                onChange={(e) => setNonStopOnly(e.target.checked)}
                            />
                            <span>Non stop only</span>
                        </label>
                    </div>
                    <button
                        className={`primary-search-btn ${!isFormValid ? 'disabled' : ''}`}
                        disabled={!isFormValid}
                        onClick={() => {
                            if (!fromLocation || String(fromLocation).trim() === '') {
                                toast.error('Please enter departure city', { position: 'top-center' });
                                return;
                            }
                            if (!toLocation || String(toLocation).trim() === '') {
                                toast.error('Please enter destination city', { position: 'top-center' });
                                return;
                            }
                            if (!travelDate) {
                                toast.error('Please select travel date', { position: 'top-center' });
                                return;
                            }

                            if (String(fromLocation).trim().toLowerCase() === String(toLocation).trim().toLowerCase()) {
                                toast.error('Origin and Destination cannot be the same', { position: 'top-center' });
                                return;
                            }

                            // Smart pick before search if data is missing
                            const POPULAR_LOCS = [
                                { name: "Indira Gandhi Intl Airport", code: "DEL", city: "Delhi", type: "airport", label: "Delhi (DEL)", subLabel: "IGI Airport", lat: 28.5562, lng: 77.1000 },
                                { name: "New Delhi", code: "NDLS", city: "Delhi", type: "station", label: "New Delhi (NDLS)", subLabel: "Railway Station", lat: 28.6141, lng: 77.2144 },
                                { name: "Chhatrapati Shivaji Intl", code: "BOM", city: "Mumbai", type: "airport", label: "Mumbai (BOM)", subLabel: "International Airport", lat: 19.0887, lng: 72.8679 },
                                { name: "Mumbai Central", code: "MMCT", city: "Mumbai", type: "station", label: "Mumbai Central (MMCT)", subLabel: "Railway Station", lat: 18.9698, lng: 72.8193 },
                                { name: "Kempegowda Intl Airport", code: "BLR", city: "Bangalore", type: "airport", label: "Bangalore (BLR)", subLabel: "International Airport", lat: 13.1989, lng: 77.7068 },
                                { name: "KSR Bengaluru City", code: "SBC", city: "Bengaluru", type: "station", label: "Bengaluru (SBC)", subLabel: "Railway Station", lat: 12.9774, lng: 77.5728 },
                                { name: "Chennai Central", code: "MAS", city: "Chennai", type: "station", label: "Chennai Central (MAS)", subLabel: "Railway Station", lat: 13.0827, lng: 80.2707 },
                                { name: "Netaji Subhash Chandra Bose Intl", code: "CCU", city: "Kolkata", type: "airport", label: "Kolkata (CCU)", subLabel: "International Airport", lat: 22.6547, lng: 88.4467 },
                                { name: "Pune Junction", code: "PUNE", city: "Pune", type: "station", label: "Pune Jn (PUNE)", subLabel: "Railway Station", lat: 18.5289, lng: 73.8744 },
                                { name: "Hyderabad Deccan", code: "HYB", city: "Hyderabad", type: "station", label: "Hyderabad (HYB)", subLabel: "Railway Station", lat: 17.3916, lng: 78.4684 }
                            ];

                            let finalFromData = fromLocationData;
                            let finalToData = toLocationData;
                            let finalFromText = fromLocation;
                            let finalToText = toLocation;

                            if (fromLocation && !fromLocationData) {
                                const match = POPULAR_LOCS.find(l =>
                                    l.city.toLowerCase() === fromLocation.toLowerCase() ||
                                    l.label.toLowerCase() === fromLocation.toLowerCase()
                                );
                                if (match) {
                                    finalFromData = match;
                                    finalFromText = match.label;
                                }
                            }
                            if (toLocation && !toLocationData) {
                                const match = POPULAR_LOCS.find(l =>
                                    l.city.toLowerCase() === toLocation.toLowerCase() ||
                                    l.label.toLowerCase() === toLocation.toLowerCase()
                                );
                                if (match) {
                                    finalToData = match;
                                    finalToText = match.label;
                                }
                            }

                            // Advanced check: Same city but different station/airport names
                            if (finalFromData && finalToData) {
                                const normalizeCity = (c) => {
                                    const city = (c || '').trim().toLowerCase();
                                    if (city === 'new delhi') return 'delhi';
                                    return city;
                                };

                                const originCity = normalizeCity(finalFromData.city);
                                const destCity = normalizeCity(finalToData.city);
                                const originCode = (finalFromData.code || '').trim().toLowerCase();
                                const destCode = (finalToData.code || '').trim().toLowerCase();

                                // If city names match OR airport/station codes match, it's the same location effectively
                                if ((originCity && destCity && originCity === destCity) ||
                                    (originCode && destCode && originCode === destCode)) {
                                    toast.error('Origin and Destination cannot be in the same city', { position: 'top-center' });
                                    return;
                                }
                            }

                            dispatch(clearRoutes());
                            dispatch(setLoading(true));
                            dispatch(setHeaderLoading(true));

                            const params = new URLSearchParams({
                                from: finalFromText,
                                to: finalToText,
                                date: travelDate ? (() => {
                                    const d = new Date(travelDate);
                                    const year = d.getFullYear();
                                    const month = String(d.getMonth() + 1).padStart(2, '0');
                                    const day = String(d.getDate()).padStart(2, '0');
                                    return `${year}-${month}-${day}`;
                                })() : '',
                                adults: passengerCounts.adults,
                                children: passengerCounts.children,
                                infants: passengerCounts.infants,
                                seatClasses: seatClasses.join(','),
                                modes: activeModes.join(','),
                                nonstop: nonStopOnly ? '1' : '0'
                            });
                            navigate(`/search-form?${params.toString()}`, {
                                state: {
                                    fromLocation: finalFromText,
                                    toLocation: finalToText,
                                    fromLocationData: finalFromData,
                                    toLocationData: finalToData,
                                    travelDate: travelDate ? travelDate.toISOString() : null,
                                    passengerCounts,
                                    seatClasses,
                                    activeModes,
                                    nonStopOnly: nonStopOnly
                                }
                            });

                            // Save search data to localStorage for persistence
                            localStorage.setItem("last_form_search", JSON.stringify({
                                fromLocation: finalFromText,
                                toLocation: finalToText,
                                fromLocationData: finalFromData,
                                toLocationData: finalToData,
                                travelDate: travelDate ? travelDate.toISOString() : null,
                                passengerCounts,
                                seatClasses,
                                activeModes,
                                nonStopOnly
                            }));
                        }}
                    >
                        Search Routes
                    </button>
                </div>
            </div>

        {/* ── Travelers bottom sheet ── */}
        {showTravelersModal && (
            <>
                {/* Backdrop */}
                <div
                    onClick={() => setShowTravelersModal(false)}
                    style={{
                        position: 'fixed', inset: 0,
                        background: 'rgba(0,0,0,0.4)',
                        zIndex: 9998,
                    }}
                />
                {/* Sheet */}
                <div
                    ref={dropdownRef}
                    style={{
                        position: 'fixed',
                        bottom: 0,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '100%',
                        maxWidth: '480px',
                        background: '#fff',
                        borderRadius: '20px 20px 0 0',
                        padding: '0 0 32px',
                        zIndex: 9999,
                        maxHeight: '85vh',
                        overflowY: 'auto',
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Handle */}
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px' }}>
                        <div style={{ width: 40, height: 4, borderRadius: 2, background: '#E2E8F0' }} />
                    </div>
                    {/* Header */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 20px 16px' }}>
                        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0F172A' }}>Travelers & Class</h2>
                        <button
                            onClick={() => setShowTravelersModal(false)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                        >
                            <X size={22} color="#0F172A" />
                        </button>
                    </div>

                    <div style={{ padding: '0 20px' }}>
                        {/* Passenger counts */}
                        {[
                            { key: 'adults', label: 'Adults', age: '18+' },
                            { key: 'children', label: 'Children', age: '2-11' },
                            { key: 'infants', label: 'Infants', age: 'under 2' }
                        ].map(({ key, label, age }) => (
                            <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid #F1F5F9' }}>
                                <div>
                                    <div style={{ fontSize: 16, fontWeight: 600, color: '#0F172A' }}>{label}</div>
                                    <div style={{ fontSize: 13, color: '#94A3B8' }}>{age}</div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setPassengerCounts(prev => ({ ...prev, [key]: Math.max(0, (prev[key] || 0) - 1) })); }}
                                        style={{ width: 36, height: 36, borderRadius: '50%', border: '1.5px solid #CBD5E1', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    ><Minus size={16} /></button>
                                    <span style={{ fontSize: 18, fontWeight: 700, minWidth: 24, textAlign: 'center' }}>{passengerCounts[key] || 0}</span>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setPassengerCounts(prev => ({ ...prev, [key]: (prev[key] || 0) + 1 })); }}
                                        style={{ width: 36, height: 36, borderRadius: '50%', border: '1.5px solid #CBD5E1', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    ><Plus size={16} /></button>
                                </div>
                            </div>
                        ))}

                        {/* Flight classes */}
                        {(activeModes.includes('Flight') || activeModes.includes('All')) && (
                            <div style={{ marginTop: 20 }}>
                                <div style={{ fontSize: 15, fontWeight: 600, color: '#0F172A', marginBottom: 12 }}>Flight Seat Class</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                    {['Economy', 'Premium', 'Business', 'First'].map(cls => {
                                        const isSel = seatClasses.includes(cls);
                                        return (
                                            <div key={cls} onClick={(e) => { e.stopPropagation(); toggleSeatClass(cls); }}
                                                style={{ padding: '8px 16px', borderRadius: 8, border: `1.5px solid ${isSel ? '#FF4E01' : '#CBD5E1'}`, background: isSel ? '#FFF7F4' : '#fff', color: isSel ? '#FF4E01' : '#475569', fontWeight: isSel ? 700 : 500, fontSize: 14, cursor: 'pointer' }}>
                                                {isSel ? '✓ ' : ''}{cls}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Train classes */}
                        {(activeModes.includes('Train') || activeModes.includes('All')) && (
                            <div style={{ marginTop: 20 }}>
                                <div style={{ fontSize: 15, fontWeight: 600, color: '#0F172A', marginBottom: 12 }}>Train Seat Class</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                    {['1A', '2A', '3A', 'SL'].map(cls => {
                                        const isSel = seatClasses.includes(cls);
                                        return (
                                            <div key={cls} onClick={(e) => { e.stopPropagation(); toggleSeatClass(cls); }}
                                                style={{ padding: '8px 16px', borderRadius: 8, border: `1.5px solid ${isSel ? '#FF4E01' : '#CBD5E1'}`, background: isSel ? '#FFF7F4' : '#fff', color: isSel ? '#FF4E01' : '#475569', fontWeight: isSel ? 700 : 500, fontSize: 14, cursor: 'pointer' }}>
                                                {isSel ? '✓ ' : ''}{cls}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Actions */}
                        <div style={{ display: 'flex', gap: 12, marginTop: 28 }}>
                            <button
                                onClick={(e) => { e.stopPropagation(); setPassengerCounts({ adults: 1, children: 0, infants: 0 }); setSeatClasses(['Economy', '3A']); }}
                                style={{ flex: 1, height: 48, border: '1.5px solid #CBD5E1', borderRadius: 10, background: '#fff', color: '#475569', fontWeight: 600, fontSize: 15, cursor: 'pointer' }}
                            >Clear</button>
                            <button
                                onClick={(e) => { e.stopPropagation(); setShowTravelersModal(false); }}
                                style={{ flex: 2, height: 48, border: 'none', borderRadius: 10, background: '#0F172A', color: '#fff', fontWeight: 600, fontSize: 15, cursor: 'pointer' }}
                            >Apply</button>
                        </div>
                    </div>
                </div>
            </>
        )}

        </section>
    );
};

export default FormSearchSection;
